import { motion } from "framer-motion";

interface IslamicPatternProps {
  className?: string;
  opacity?: number;
}

export const IslamicPattern = ({ className = "", opacity = 0.10 }: IslamicPatternProps) => {
  const patternWidth = 180;
  const patternHeight = 300;
  const circleRadius = 90;
  
  return (
    <div className={`absolute inset-0 pointer-events-none overflow-hidden ${className}`}>
      <motion.svg
        className="w-full h-full"
        style={{ opacity }}
        preserveAspectRatio="xMidYMid slice"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 2.0, ease: "easeOut" }}
      >
        <defs>
          {/* Carved inset filter — top-left light source */}
          <filter id="carved-circles" x="-10%" y="-10%" width="120%" height="120%">
            {/* Light edge (top-left highlight) */}
            <feOffset dx="-0.4" dy="-0.4" in="SourceAlpha" result="lo" />
            <feGaussianBlur stdDeviation="0.6" in="lo" result="lb" />
            <feFlood floodColor="hsl(150, 30%, 65%)" floodOpacity="0.25" result="lc" />
            <feComposite in="lc" in2="lb" operator="in" result="ls" />
            {/* Dark edge (bottom-right shadow) */}
            <feOffset dx="0.5" dy="0.5" in="SourceAlpha" result="do" />
            <feGaussianBlur stdDeviation="0.8" in="do" result="db" />
            <feFlood floodColor="hsl(155, 40%, 5%)" floodOpacity="0.3" result="dc" />
            <feComposite in="dc" in2="db" operator="in" result="ds" />
            <feMerge>
              <feMergeNode in="ds" />
              <feMergeNode in="ls" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Radial fade — stronger at centre, fades at edges */}
          <radialGradient id="circle-fade" cx="50%" cy="45%" r="60%">
            <stop offset="0%" stopColor="white" stopOpacity="1" />
            <stop offset="50%" stopColor="white" stopOpacity="0.6" />
            <stop offset="100%" stopColor="white" stopOpacity="0.1" />
          </radialGradient>
          <mask id="circle-mask">
            <rect width="100%" height="100%" fill="url(#circle-fade)" />
          </mask>

          <pattern
            id="islamic-circles-pattern"
            x="0"
            y="0"
            width={patternWidth}
            height={patternHeight}
            patternUnits="userSpaceOnUse"
          >
            <g stroke="hsl(150, 30%, 50%)" fill="none" strokeWidth="0.5" filter="url(#carved-circles)">
              {/* Vertical lines */}
              <line x1={patternWidth / 2} y1="0" x2={patternWidth / 2} y2={patternHeight} />
              <line x1="0" y1="0" x2="0" y2={patternHeight} />
              <line x1={patternWidth} y1="0" x2={patternWidth} y2={patternHeight} />
              
              {/* Main circles */}
              <circle cx={patternWidth / 2} cy="0" r={circleRadius} />
              <circle cx="0" cy="0" r={circleRadius} />
              <circle cx={patternWidth} cy="0" r={circleRadius} />
              
              <circle cx="0" cy={patternHeight / 2} r={circleRadius} />
              <circle cx={patternWidth} cy={patternHeight / 2} r={circleRadius} />
              <circle cx={patternWidth / 2} cy={patternHeight / 2} r={circleRadius} />
              
              <circle cx={patternWidth / 2} cy={patternHeight} r={circleRadius} />
              <circle cx="0" cy={patternHeight} r={circleRadius} />
              <circle cx={patternWidth} cy={patternHeight} r={circleRadius} />
              
              <circle cx="0" cy={patternHeight / 4} r={circleRadius} />
              <circle cx={patternWidth} cy={patternHeight / 4} r={circleRadius} />
              <circle cx={patternWidth / 2} cy={patternHeight / 4} r={circleRadius} />
              
              <circle cx="0" cy={(patternHeight * 3) / 4} r={circleRadius} />
              <circle cx={patternWidth} cy={(patternHeight * 3) / 4} r={circleRadius} />
              <circle cx={patternWidth / 2} cy={(patternHeight * 3) / 4} r={circleRadius} />
            </g>
          </pattern>
        </defs>
        
        <g mask="url(#circle-mask)">
          <rect width="100%" height="100%" fill="url(#islamic-circles-pattern)" />
        </g>
      </motion.svg>
    </div>
  );
};
